<template>
  <transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="transform scale-105 opacity-0"
    enter-to-class="transform scale-100 opacity-100"
  >
    <div v-if="isOpen" class="cn-modal">
      <div
        ref="modal"
        class="cn-modal__block sm:cut-corner"
        :style="{ width: width, maxHeight: height }"
      >
        <span class="cn-modal__close">
          <button class="cn-modal__close-button" @click="closeModal">
            <UiIcon name="close" />
          </button>
        </span>
        <div class="cn-modal__content">
          <div>
            <div v-if="title" class="cn-modal__title">
              {{ title }}
            </div>
            <div v-if="description" class="cn-modal__description">
              {{ description }}
            </div>
          </div>
          <div class="cn-modal__body">
            <slot name="default" />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useOpenClose, useBodyScrollLock } from '@/composables';
import UiIcon from '@/components/ui/UiIcon';

interface Props {
  title?: string;
  description?: string;
  width?: string;
  height?: string;
}

withDefaults(defineProps<Props>(), {
  width: '700px',
  title: '',
  description: '',
  height: '',
});
const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { isOpen, open, close } = useOpenClose();
useBodyScrollLock(() => isOpen.value);

const modal = ref(null);
const closeModal = () => {
  close();
  emit('close');
};
onClickOutside(modal, () => closeModal());

defineExpose({ isOpen, open, close });
</script>

<style lang="scss">
.cn-modal {
  --ab-canyon-modal-color-bg: var(--ab-canyon-color-dark);
  --ab-canyon-modal-content-color-bg: var(--ab-canyon-color-light);
  --ab-canyon-modal-description-color: var(--ab-canyon-color-neutral-dark);
  --ab-canyon-modal-title-color: var(--ab-canyon-color-dark);

  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-y: auto;
  background-color: color-mix(
    in srgb,
    var(--ab-canyon-modal-color-bg),
    transparent 60%
  );

  @media (min-width: 640px) {
    padding: var(--size-base);
  }
  &__close {
    top: calc(var(--size-base) / 2);
    right: calc(var(--size-base) / 4);
    position: absolute;
    @media (min-width: 656px) {
      top: var(--size-lg);
      right: var(--size-lg);
    }
  }
  &__close-button {
    padding: var(--size-xs);
    .cn-icon {
      width: var(--size-xl);
      height: var(--size-xl);
    }
  }
  &__block {
    position: relative;
    z-index: 50;
    margin: auto;
    height: 100%;
    background-color: var(--ab-canyon-modal-content-color-bg);
    box-shadow:
      0 0 #00000000,
      0 0 #00000000,
      0 10px 15px -3px #0000001a,
      0 4px 6px -4px #0000001a;

    @media (min-width: 640px) {
      height: auto;
    }
  }
  &__content {
    padding: var(--size-base);
    flex-direction: column;
    height: 100%;
    display: flex;
    @media (min-width: 656px) {
      padding: var(--size-5xl);
    }
  }
  &__title {
    font-size: 1.125rem;
    line-height: 1.75rem;
    font-weight: 700;
    text-transform: uppercase;
    color: var(--ab-canyon-modal-title-color);

    @media (min-width: 640px) {
      font-size: 1.5rem;
      line-height: 2rem;
    }
  }
  &__description {
    margin-top: var(--size-lg);
    max-width: 450px;
    color: var(--ab-canyon-modal-description-color);
  }
  &__body {
    overflow-y: auto;
    flex-grow: 1;
    margin-top: var(--size-base);
    @media (min-width: 656px) {
      overflow: visible;
    }
  }
}
</style>
